import React, { useEffect } from 'react';
import { useMeasure } from 'react-use';
import { IconMenuBarsOutlineDuo18 } from 'nucleo-ui-outline-duo-18';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import { useRouteChrome } from '@/components/RouteChrome';
import { useSidebarCollapse } from '@/components/SidebarCollapse';
import { useAppConfig } from '@/hooks/useAppConfig';
import { sidebarIconStrokeWidth } from '@/modules/corporate/modules/admin/components/Sidebar/nav-icon';
import {
  SIDEBAR_FOCUS_RING,
  SIDEBAR_ROW_REST,
} from '@/modules/corporate/modules/admin/components/Sidebar/theme';

import DefaultLeftNode from '../components/DefaultLeftNode';
import DefaultRightNodeLazy from '../components/DefaultRightNode/index.lazy';
import type { DefaultHeaderProps } from '../types';

// The unified sidebar has no room for itself on mobile (AppShell hides the
// `<aside>` below `md`), so this is its only way onto the screen there — the
// desktop header's collapse toggle isn't shown here since there's no rail state.
const MobileSidebarTrigger = ({ onPress }: { onPress: () => void }) => (
  <button
    aria-label="Open navigation menu"
    className={cn(
      'flex h-32 w-32 flex-shrink-0 cursor-pointer items-center justify-center rounded-8 transition-colors duration-100',
      SIDEBAR_ROW_REST,
      SIDEBAR_FOCUS_RING,
    )}
    type="button"
    onClick={onPress}>
    <IconMenuBarsOutlineDuo18 className="h-20 w-20" strokeWidth={sidebarIconStrokeWidth(20)} />
  </button>
);

function renderCenterNode({
  CenterNode,
  className,
}: Pick<DefaultHeaderProps, 'CenterNode' | 'className'>) {
  if (CenterNode) return <Box className={className}>{CenterNode}</Box>;

  return null;
}

const DefaultHeader: React.FC<DefaultHeaderProps> = React.memo((props) => {
  const { RightNode, LeftNode, CenterNode, className, showShadow, isDisabled } = props;
  const { isLandScapeMode } = useScreen();
  const {
    ui: { isUnifiedSidebar },
  } = useAppConfig();
  const { openMobileNav } = useSidebarCollapse();
  const chrome = useRouteChrome();
  const showSidebarTrigger = isUnifiedSidebar && chrome.sidebar;

  const [headerRef, { height: computedHeight }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    document.body.style.setProperty('--ly-header-height', `${computedHeight}px`);
    return () => {
      document.body.style.removeProperty('--ly-header-height');
    };
  }, [computedHeight]);

  const HeaderLeftNode = LeftNode ? LeftNode : <DefaultLeftNode textColor="text-text-primary" />;
  const HeaderRightNode = RightNode !== undefined ? RightNode : <DefaultRightNodeLazy />;
  const height = isLandScapeMode ? 'h-[52px]' : 'h-[56px]';
  return (
    <Box
      ref={headerRef}
      className={cn(
        'insert-padding-top-screen sticky top-0 z-50 w-full max-w-full bg-bg-primary',
        showShadow ? 'shadow-01' : undefined,
        isDisabled ? 'pointer-events-none' : undefined,
        className,
      )}>
      <Box
        className={cn('flex w-full flex-row items-center justify-between bg-bg-primary', height)}>
        {LeftNode === null ? null : (
          <Box className="flex flex-shrink-0 flex-row items-center justify-start gap-8">
            {showSidebarTrigger ? <MobileSidebarTrigger onPress={openMobileNav} /> : null}
            {HeaderLeftNode}
          </Box>
        )}
        {renderCenterNode({
          CenterNode,
          className: LeftNode === null ? 'ps-24' : undefined,
        })}
        <Box className="flex flex-shrink-0 flex-row justify-end">{HeaderRightNode}</Box>
      </Box>
    </Box>
  );
});

export default DefaultHeader;
